import React, { useEffect, useState, useCallback, useRef } from "react";
import { HeaderPage } from "../base/headerPage";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import useContractElementSummary from "./service";
import ContractDetailsSection from "./contractDetailsSection";
import ReportSection from "./reportSection";
import { alignClass } from "./valueFormat";
import {
  getVersionDelta,
  formatDelta,
  formatDeltaPercent,
  versionLabel,
} from "./versionDelta";
import "./contractElementSummary.scss";

const TIER_KEYWORD = "Tier";

const formatValue = (value, unitName, format) => {
  if (value === null || value === undefined) return "";
  const num = Number(value);
  if (isNaN(num)) return String(value);

  if (format && format.startsWith("$")) {
    return (
      "$ " +
      num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  if (unitName === "currency") {
    return (
      "$ " +
      num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  if (unitName === "percentage") {
    return num.toFixed(1) + " %";
  }

  if (format) {
    const decimalMatch = format.match(/\.(\d+|0+)/);
    const decimals = decimalMatch ? decimalMatch[1].length : 0;
    return num.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getBadgeClass = (level) => {
  if (!level) return "";
  const lower = level.toLowerCase();
  if (lower === "specialty") return "badge-specialty";
  if (lower === "comp plan" || lower === "plan") return "badge-comp-plan";
  if (lower === "position") return "badge-position";
  return "";
};

/** Screenshot badges read PLAN / POSITION / SPECIALTY rather than the proc's "Comp Plan". */
const badgeLabel = (level) => {
  if (!level) return "";
  const lower = level.toLowerCase();
  if (lower === "comp plan" || lower === "plan") return "Plan";
  if (lower === "position") return "Position";
  if (lower === "specialty") return "Specialty";
  return level;
};

/**
 * version_seq is the proc's own oldest-to-newest ordering of an element's versions. The
 * effective date and version are the fallback for rows that arrive without it.
 */
const compareVersions = (a, b) => {
  if (a.version_seq != null && b.version_seq != null) {
    return Number(a.version_seq) - Number(b.version_seq);
  }

  const byDate = String(a.eff_start_date || "").localeCompare(
    String(b.eff_start_date || "")
  );
  return byDate !== 0
    ? byDate
    : Number(a.element_version || 0) - Number(b.element_version || 0);
};

/**
 * The proc returns one row per period version of an element's value, so the rows are
 * folded back into a single element carrying its whole history. The report shows the
 * element once at its newest value and lists the versions in the comparison popover.
 *
 * Versions of an element share a contract element assignment, which report_order comes
 * from and is therefore constant across them — that plus the name identifies the element
 * within a position.
 */
const groupElementVersions = (rows) => {
  const elements = [];
  const byElement = new Map();

  rows.forEach((row) => {
    const key = `${row.report_order}|${row.contract_element_name}`;
    if (!byElement.has(key)) {
      const element = { key, versions: [] };
      byElement.set(key, element);
      elements.push(element);
    }
    byElement.get(key).versions.push(row);
  });

  elements.forEach((element) => {
    element.versions.sort(compareVersions);
    element.current = element.versions[element.versions.length - 1];
  });

  return elements;
};

/**
 * Elements stay grouped by their KPI, but each group is now headed by the KPI's type
 * name and description rather than the KPI name, which the stored proc returns on
 * every row. The KPI name is the fallback for tenants whose KPIs carry no type.
 */
const groupByKpi = (rows) => {
  const groups = [];
  const byKpi = new Map();

  rows
    .slice()
    .sort((a, b) => a.report_order - b.report_order)
    .forEach((row) => {
      const key = row.kpi_name;
      if (!byKpi.has(key)) {
        const group = {
          key,
          title: row.kpi_type_name || row.kpi_name,
          description: row.kpi_desc || "",
          rows: [],
        };
        byKpi.set(key, group);
        groups.push(group);
      }
      byKpi.get(key).rows.push(row);
    });

  return groups.map((group) => ({
    ...group,
    elements: groupElementVersions(group.rows),
  }));
};

const isTierElement = (name) => {
  return name && name.includes(TIER_KEYWORD);
};

const classifyTierElement = (name) => {
  const lower = name.toLowerCase();
  if (lower.includes("threshold")) return "threshold";
  if (lower.includes("multiplier")) return "multiplier";
  if (lower.includes("rate")) return "rate";
  return "threshold";
};

/**
 * Sits over an element that has held more than one value, listing every period version
 * oldest to newest so the history behind the delta badge is readable without leaving the
 * report. The Delta row is the movement between the newest version and the one before
 * it. Shown on hover and on keyboard focus.
 */
const VersionComparison = ({ versions, current, delta, formatAmount }) => {
  const percent = delta ? formatDeltaPercent(delta) : "";

  return (
    <div className="ces-version-popover" data-testid="ces-version-popover">
      <div className="ces-version-popover-title">Version comparison</div>
      {versions.map((row, index) => {
        const label = versionLabel(row.period_name, row.eff_start_date);

        return (
          <div
            className={`ces-version-row${
              row === current ? " ces-version-current" : ""
            }`}
            key={`${label}-${index}`}
          >
            <span>{label || `Version ${index + 1}`}</span>
            <span>{formatAmount(row.value_amt)}</span>
          </div>
        );
      })}
      {delta && (
        <div className="ces-version-row ces-version-delta">
          <span>Delta</span>
          <span className={`ces-delta-pill ces-delta-${delta.direction}`}>
            {percent || formatAmount(delta.change)}
          </span>
        </div>
      )}
    </div>
  );
};

const ElementCard = ({ element }) => {
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const { current, versions } = element;

  const formatAmount = (amount) =>
    formatValue(
      amount,
      current.system_unit_name,
      current.unit_type_report_format
    );

  const value = formatAmount(current.value_amt);
  const delta = getVersionDelta(current);
  // An element that has only ever held one value has no history worth opening.
  const hasHistory = versions.length > 1;

  const cardClass = [
    "ces-element-card",
    delta ? `ces-element-${delta.direction}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cardClass}
      data-testid="ces-element-card"
      onMouseEnter={hasHistory ? () => setComparisonOpen(true) : undefined}
      onMouseLeave={hasHistory ? () => setComparisonOpen(false) : undefined}
      onFocus={hasHistory ? () => setComparisonOpen(true) : undefined}
      onBlur={hasHistory ? () => setComparisonOpen(false) : undefined}
      tabIndex={hasHistory ? 0 : undefined}
    >
      <div className="ces-element-header">
        <div className="ces-element-info">
          <div className="ces-element-name-row">
            <span className="ces-element-name">{current.contract_element_name}</span>
            {delta && (
              <span
                className={`ces-delta-badge ces-delta-${delta.direction}`}
                data-testid="ces-delta-badge"
              >
                {formatDelta(delta, formatAmount)}
              </span>
            )}
          </div>
          <div className="ces-element-source-row">
            <span className="ces-element-source">{current.contr_el_source_name}</span>
            {current.entity_ref_level && (
              <span className={`ces-badge ${getBadgeClass(current.entity_ref_level)}`}>
                {badgeLabel(current.entity_ref_level)}
              </span>
            )}
          </div>
        </div>
        <div className={`ces-element-value ${alignClass(value)}`}>{value}</div>
      </div>
      {hasHistory && comparisonOpen && (
        <VersionComparison
          versions={versions}
          current={current}
          delta={delta}
          formatAmount={formatAmount}
        />
      )}
    </div>
  );
};

const extractTierNumber = (name) => {
  const match = name.match(/Tier\s+(\d+)/i);
  return match ? parseInt(match[1], 10) : -1;
};

const COLUMN_HEADERS = {
  threshold: "wRVU Threshold",
  multiplier: "Multiplier",
  rate: "Rate per wRVU",
};

const EMPTY_TIER_CELL = "\u2014";

const formatTierAmount = (element, amount) =>
  formatValue(
    amount,
    element.current.system_unit_name,
    element.current.unit_type_report_format
  );

/**
 * One cell in the Tiers table. Empty slots render an em dash so the columns still
 * line up when a tier has a threshold and rate but no multiplier.
 */
const TierCell = ({ element, colType, tierNum }) => {
  const [comparisonOpen, setComparisonOpen] = useState(false);

  if (!element) {
    return (
      <div
        className={`ces-tier-cell ces-tier-cell-${colType}`}
        data-testid={`ces-tier-cell-${colType}-${tierNum}`}
      >
        <span className="ces-tier-cell-value">{EMPTY_TIER_CELL}</span>
      </div>
    );
  }

  const { current, versions } = element;
  const formatAmount = (amount) => formatTierAmount(element, amount);
  const value = formatAmount(current.value_amt);
  const delta = getVersionDelta(current);
  const hasHistory = versions.length > 1;

  return (
    <div
      className={`ces-tier-cell ces-tier-cell-${colType}`}
      data-testid={`ces-tier-cell-${colType}-${tierNum}`}
      onMouseEnter={hasHistory ? () => setComparisonOpen(true) : undefined}
      onMouseLeave={hasHistory ? () => setComparisonOpen(false) : undefined}
      onFocus={hasHistory ? () => setComparisonOpen(true) : undefined}
      onBlur={hasHistory ? () => setComparisonOpen(false) : undefined}
      tabIndex={hasHistory ? 0 : undefined}
    >
      <span className={`ces-tier-cell-value ${alignClass(value)}`}>{value}</span>
      {hasHistory && comparisonOpen && (
        <VersionComparison
          versions={versions}
          current={current}
          delta={delta}
          formatAmount={formatAmount}
        />
      )}
    </div>
  );
};

const TiersSection = ({ tierElements }) => {
  const [isOpen, setIsOpen] = useState(false);

  const tierMap = {};
  tierElements.forEach((el) => {
    const name = el.current.contract_element_name;
    const tierNum = extractTierNumber(name);
    if (!tierMap[tierNum]) tierMap[tierNum] = {};
    tierMap[tierNum][classifyTierElement(name)] = el;
  });

  const tierNumbers = Object.keys(tierMap)
    .map(Number)
    .sort((a, b) => a - b);

  const hasThresholds = tierElements.some(
    (e) => classifyTierElement(e.current.contract_element_name) === "threshold"
  );
  const hasMultipliers = tierElements.some(
    (e) => classifyTierElement(e.current.contract_element_name) === "multiplier"
  );
  const hasRates = tierElements.some(
    (e) => classifyTierElement(e.current.contract_element_name) === "rate"
  );

  const activeColumns = [
    hasThresholds && "threshold",
    hasMultipliers && "multiplier",
    hasRates && "rate",
  ].filter(Boolean);

  const colClass =
    activeColumns.length === 3
      ? "ces-tiers-3col"
      : activeColumns.length === 2
      ? "ces-tiers-2col"
      : "ces-tiers-1col";

  const sample = tierElements[0]?.current;
  const source = sample?.contr_el_source_name;
  const level = sample?.entity_ref_level;

  return (
    <div className="ces-tiers-section">
      <div
        className="ces-tiers-header"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setIsOpen(!isOpen);
        }}
        data-testid="ces-tiers-toggle"
      >
        <span className="ces-tiers-title">Tiers – Thresholds &amp; Rates</span>
        <span className="ces-tiers-meta">
          {tierNumbers.length} tier{tierNumbers.length === 1 ? "" : "s"}
        </span>
        {source && (
          <span className="ces-tiers-source">
            <span className="ces-element-source">{source}</span>
            {level && (
              <span className={`ces-badge ${getBadgeClass(level)}`}>
                {badgeLabel(level)}
              </span>
            )}
          </span>
        )}
        <span className={`ces-tiers-toggle ${isOpen ? "expanded" : ""}`}>
          &#9660;
        </span>
      </div>
      {isOpen && (
        <div className={`ces-tiers-table ${colClass}`}>
          <div className="ces-tiers-table-head">
            <span>Tier</span>
            {activeColumns.map((colType) => (
              <span key={colType}>{COLUMN_HEADERS[colType]}</span>
            ))}
          </div>
          {tierNumbers.map((tierNum) => (
            <div className="ces-tiers-table-row" key={tierNum}>
              <span className="ces-tier-name">Tier {tierNum}</span>
              {activeColumns.map((colType) => (
                <TierCell
                  key={`${tierNum}-${colType}`}
                  element={tierMap[tierNum]?.[colType]}
                  colType={colType}
                  tierNum={tierNum}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const KpiSection = ({ title, description, elements }) => {
  const regularElements = elements.filter(
    (e) => !isTierElement(e.current.contract_element_name)
  );
  const tierElements = elements.filter((e) =>
    isTierElement(e.current.contract_element_name)
  );
  const isPanel = tierElements.length > 0;

  return (
    <div
      className={`ces-kpi-section${isPanel ? " ces-kpi-panel" : ""}`}
      data-testid="ces-kpi-section"
    >
      <div className="ces-kpi-heading">
        <span className="ces-kpi-title">{title}</span>
        {description && <span className="ces-kpi-desc">{description}</span>}
      </div>
      {regularElements.length > 0 && (
        <div className="ces-elements-grid">
          {regularElements.map((el) => (
            <ElementCard key={el.key} element={el} />
          ))}
        </div>
      )}
      {tierElements.length > 0 && <TiersSection tierElements={tierElements} />}
    </div>
  );
};

/**
 * The utilization columns are DECIMAL(_,8), so the stored proc returns them as "0.30000000".
 * Anything that parses as a decimal is capped at two places to stay readable in the compact
 * horizontal layout; identifiers and free text (Employee ID, Specialty CS) pass through as-is.
 */
const formatProviderValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const text = String(value).trim();
  if (!/^-?\d+\.\d+$/.test(text)) return text;

  const num = Number(text);
  return isNaN(num) ? text : num.toFixed(2);
};

const ProviderProfileSection = ({ fields, values, loading }) => {
  // No fields means the comp_attr configuration has not loaded, so there is nothing to lay out.
  // Once it has, the section stays visible through the lookup rather than disappearing.
  if (fields.length === 0 || (!values && !loading)) return null;

  return (
    <ReportSection
      title="Provider Profile Details"
      subtitle="Comp attributes and utilization"
      testId="ces-provider-section"
    >
      <div className="ces-provider-grid">
        {fields.map((field) => {
          const value = loading
            ? "Loading..."
            : formatProviderValue(values?.[field.filter_column_name]);

          return (
            <div className="ces-provider-field" key={field.filter_column_name}>
              <div className="ces-provider-label">{field.filter_label}</div>
              <div className={`ces-provider-value ${alignClass(value)}`}>{value}</div>
            </div>
          );
        })}
      </div>
    </ReportSection>
  );
};

const ContractElementSummary = (props) => {
  const { readAccess, writeAccess, ...restProps } = props;

  const [state, actions] = useContractElementSummary();
  const {
    reportData,
    loading,
    error,
    noData,
    compPlanOptions,
    positionOptions,
    compPlanLoading,
    positionLoading,
    providerFields,
    providerValues,
    providerLoading,
    contractRows,
    contractLoading,
  } = state;

  const [selectedCompPlan, setSelectedCompPlan] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const didInitialRunRef = useRef(false);

  const headerConfig = {
    title: "Comp Plan Summary",
    id: "contract-element-summary",
  };

  const handleLoadReport = useCallback(() => {
    if (!selectedCompPlan || !selectedPosition) return;
    actions.getContractElementReport({
      in_comp_plan_key: selectedCompPlan.comp_plan_mstr_key,
      in_position_key: selectedPosition.org_position_key,
    });
    actions.getProviderProfile(selectedPosition.org_position_key);
    actions.getPositionContractDetails(selectedPosition.org_position_key);
  }, [selectedCompPlan, selectedPosition, actions]);

  // Load the comp plan filter options on mount and default to the first one.
  useEffect(() => {
    (async () => {
      const compPlans = await actions.getCompPlanFilter();
      if (compPlans && compPlans.length > 0) {
        setSelectedCompPlan(compPlans[0]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Comp attribute labels are tenant configuration, so they only need loading once.
  useEffect(() => {
    actions.getProviderProfileFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cascade: when the selected comp plan changes, reload the position options
  // for that comp plan and default to the first available position.
  useEffect(() => {
    if (!selectedCompPlan) return;
    (async () => {
      const positions = await actions.getPositionFilter(selectedCompPlan.comp_plan_mstr_key);
      setSelectedPosition(positions && positions.length > 0 ? positions[0] : null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompPlan?.comp_plan_mstr_key]);

  // Auto-run the report once, after the initial comp plan + position are ready.
  useEffect(() => {
    if (!didInitialRunRef.current && selectedCompPlan && selectedPosition) {
      didInitialRunRef.current = true;
      handleLoadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompPlan, selectedPosition]);

  const kpiGroups = groupByKpi(reportData);

  return (
    <div className="contract-element-summary">
      <div className="ces-report-shell">
        <div className="ces-report-top">
          <div className="content-head ces-report-panel-head">
            <HeaderPage pageProps={restProps} headerConfig={headerConfig} />
          </div>

          <div className="ces-filters">
            <div className="ces-filter-item">
              <label htmlFor="ces-comp-plan">Comp Plan</label>
              <DropDownList
                id="ces-comp-plan"
                data={compPlanOptions}
                textField="comp_plan_name"
                dataItemKey="comp_plan_mstr_key"
                value={selectedCompPlan}
                loading={compPlanLoading}
                onChange={(e) => setSelectedCompPlan(e.target.value)}
                style={{ width: "250px" }}
              />
            </div>
            <div className="ces-filter-item">
              <label htmlFor="ces-position">Position Name</label>
              <DropDownList
                id="ces-position"
                data={positionOptions}
                textField="position_name"
                dataItemKey="org_position_key"
                value={selectedPosition}
                loading={positionLoading}
                onChange={(e) => setSelectedPosition(e.target.value)}
                style={{ width: "280px" }}
              />
            </div>
            <div className="ces-filter-btn">
              <Button
                primary={true}
                className="primarybtn"
                onClick={handleLoadReport}
                disabled={
                  loading ||
                  compPlanLoading ||
                  positionLoading ||
                  !selectedCompPlan ||
                  !selectedPosition
                }
              >
                Run
              </Button>
            </div>
          </div>
        </div>

        <div className="ces-report-scroll">
          {loading && <div className="ces-loading">Loading report data...</div>}

          {error && !loading && (
            <div className="ces-error" data-testid="ces-error">
              Error: {error}
            </div>
          )}

          {!loading && !error && (
            <ProviderProfileSection
              fields={providerFields}
              values={providerValues}
              loading={providerLoading}
            />
          )}

          {!loading && !error && noData && (
            <div className="ces-empty">Report data not found</div>
          )}

          {!loading && !error && kpiGroups.length > 0 && (
            <ReportSection
              title="Comp Plan Details"
              subtitle="Plan inputs, draw pay and wRVU tiers"
              testId="ces-comp-plan-section"
            >
              {kpiGroups.map((kpi) => (
                <KpiSection
                  key={kpi.key}
                  title={kpi.title}
                  description={kpi.description}
                  elements={kpi.elements}
                />
              ))}
            </ReportSection>
          )}

          {!loading && !error && (
            <ContractDetailsSection
              rows={contractRows}
              loading={contractLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractElementSummary;
